import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PartnerUser } from '../types';
import {
  isValidPortalLogin,
  PARTNER_PORTAL_EMAIL,
  PARTNER_PORTAL_NAME,
} from '../utils/demoAuth';

const STORAGE_KEY = 'chisme_partner_user';
const PORTAL_USER_ID = 'portal-user';

type PartnerAuthContextValue = {
  user: PartnerUser | null;
  isLoading: boolean;
  loginWithPassword: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
};

const PartnerAuthContext = createContext<PartnerAuthContextValue | null>(null);

function readStoredUser(): PartnerUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PartnerUser;
    if (parsed.id !== PORTAL_USER_ID) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export const PartnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PartnerUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUser(readStoredUser());
    setIsLoading(false);
  }, []);

  const persistUser = useCallback((next: PartnerUser | null) => {
    setUser(next);
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      await new Promise((resolve) => setTimeout(resolve, 400));

      if (!isValidPortalLogin(email, password)) {
        return false;
      }

      persistUser({
        id: PORTAL_USER_ID,
        email: PARTNER_PORTAL_EMAIL,
        name: PARTNER_PORTAL_NAME,
        provider: 'email',
      });
      return true;
    },
    [persistUser],
  );

  const logout = useCallback(() => {
    persistUser(null);
  }, [persistUser]);

  const value = useMemo(
    () => ({ user, isLoading, loginWithPassword, logout }),
    [user, isLoading, loginWithPassword, logout],
  );

  return <PartnerAuthContext.Provider value={value}>{children}</PartnerAuthContext.Provider>;
};

export function usePartnerAuth(): PartnerAuthContextValue {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) {
    throw new Error('usePartnerAuth must be used within PartnerAuthProvider');
  }
  return ctx;
}
